import { useEffect, useState, useCallback, createContext, useContext, useRef, lazy, Suspense } from "react";
import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { I18nProvider, useI18n } from "@/i18n";
import { BrandingProvider } from "@/contexts/BrandingContext";
import { ThemeProvider } from "@/contexts/ThemeContext";

// Public pages - static imports (critical path)
import LandingPage from "@/pages/LandingPage";
import PropertiesPage from "@/pages/PropertiesPage";
import PropertyDetailPage from "@/pages/PropertyDetailPage";
import MapPage from "@/pages/MapPage";
import AboutPage from "@/pages/AboutPage";
import FaqPage from "@/pages/FaqPage";
import TermsPage from "@/pages/TermsPage";
import PostsPage from "@/pages/PostsPage";
import PostDetailPage from "@/pages/PostDetailPage";
import OwnerPage from "@/pages/OwnerPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import BancardCheckoutPage from "@/pages/BancardCheckoutPage";
import BrandingPreviewPublicPage from "@/pages/BrandingPreviewPublicPage";
import QrLandingPage from "@/pages/QrLandingPage";
import QrStatsPage from "@/pages/QrStatsPage";
import WhatsAppBubble from "@/components/WhatsAppBubble";
import PropertyPopup from "@/components/PropertyPopup";
import { logger } from '@/lib/logger';

// Authenticated user pages - lazy loaded
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const GuestDashboardPage = lazy(() => import("@/pages/GuestDashboardPage"));
const GuestReservationsPage = lazy(() => import("@/pages/GuestReservationsPage"));
const ReservationDetailPage = lazy(() => import("@/pages/ReservationDetailPage"));
const UpcomingTripsPage = lazy(() => import("@/pages/UpcomingTripsPage"));
const ChatPage = lazy(() => import("@/pages/ChatPage"));
const WishlistPage = lazy(() => import("@/pages/WishlistPage"));
const VerificationPage = lazy(() => import("@/pages/VerificationPage"));
const NotificationsPage = lazy(() => import("@/pages/NotificationsPage"));
const CheckInPage = lazy(() => import("@/pages/CheckInPage"));

// Admin pages - lazy loaded
const ReservationsPage = lazy(() => import("@/pages/ReservationsPage"));
const CalendarPage = lazy(() => import("@/pages/CalendarPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const ReportsPage = lazy(() => import("@/pages/ReportsPage"));
const AdminPropertiesPage = lazy(() => import("@/pages/AdminPropertiesPage"));
const CouponsPage = lazy(() => import("@/pages/CouponsPage"));
const EmailsPage = lazy(() => import("@/pages/EmailsPage"));
const BrandingPage = lazy(() => import("@/pages/BrandingPage"));
const EventsPage = lazy(() => import("@/pages/EventsPage"));
const AdminPostsPage = lazy(() => import("@/pages/AdminPostsPage"));
const AirbnbImportPage = lazy(() => import("@/pages/AirbnbImportPage"));
const AdminBookingRequestsPage = lazy(() => import("@/pages/AdminBookingRequestsPage"));
const InstagramPage = lazy(() => import("@/pages/InstagramPage"));
const SeasonalCouponsPage = lazy(() => import("@/pages/SeasonalCouponsPage"));
const NewsletterPage = lazy(() => import("@/pages/NewsletterPage"));
const UsersPage = lazy(() => import("@/pages/UsersPage"));
const PaymentDataPage = lazy(() => import("@/pages/PaymentDataPage"));
const FilesPage = lazy(() => import("@/pages/FilesPage"));

// Auth page - public but lazy
const AuthPage = lazy(() => import("@/pages/AuthPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const GestionApp = lazy(() => import("@/gestion/GestionApp"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with credentials
const api = axios.create({
  baseURL: API,
  withCredentials: true
});


// Error Boundary to prevent blank screens
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, info) { logger.error('App Error:', error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F5F4F0', fontFamily: 'Outfit, sans-serif' }}>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <h2 style={{ color: '#1C1917', fontSize: '1.5rem', marginBottom: '1rem' }}>Algo salio mal</h2>
            <p style={{ color: '#78716C', marginBottom: '1.5rem' }}>Ocurrio un error inesperado.</p>
            <button onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
              style={{ background: '#264639', color: '#fff', padding: '0.75rem 2rem', borderRadius: '0.5rem', border: 'none', cursor: 'pointer', fontSize: '1rem' }}>
              Recargar página
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const brandTitle = 'Estadía 365 | Alojamientos en Paraguay, Argentina y Brasil';
    const isQrPage = () => window.location.pathname.startsWith('/qr') || window.location.pathname.startsWith('/gestion');
    if (!isQrPage()) document.title = brandTitle;
    const titleObserver = new MutationObserver(() => {
      if (!isQrPage() && document.title !== brandTitle) {
        document.title = brandTitle;
      }
    });
    const titleEl = document.querySelector('title');
    if (titleEl) titleObserver.observe(titleEl, { childList: true });
    return () => titleObserver.disconnect();
  }, []);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=') || window.location.pathname.startsWith('/gestion')) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const loginWithGoogle = () => {
    const authServiceUrl = process.env.REACT_APP_AUTH_SERVICE_URL;
    // Save current page so user returns after login
    const returnTo = window.location.pathname + window.location.search;
    if (returnTo && returnTo !== '/' && returnTo !== '/auth') {
      sessionStorage.setItem('returnAfterLogin', returnTo);
    }
    const redirectUrl = window.location.origin;
    window.location.href = `${authServiceUrl}/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const login = () => {
    // Direct Google OAuth - skip intermediate auth page
    loginWithGoogle();
  };

  const loginWithEmail = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    setUser(response.data.user);
    return response.data;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
      setUser(null);
      toast.success('Sesión cerrada');
    } catch (_) { toast.error('Error al cargar datos'); }
  };

  const value = {
    user,
    setUser,
    loading,
    login,
    loginWithGoogle,
    loginWithEmail,
    logout,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin' || user?.role === 'super_admin',
    isSuperAdmin: user?.role === 'super_admin'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await api.post('/auth/session', { session_id: sessionId });
          setUser(response.data.user);
          toast.success('¡Bienvenido!');
          const returnTo = sessionStorage.getItem('returnAfterLogin');
          sessionStorage.removeItem('returnAfterLogin');
          if (returnTo) {
            navigate(returnTo, { replace: true });
          } else {
            const userRole = response.data.user?.role;
            const isAdminUser = userRole === 'admin' || userRole === 'super_admin';
            navigate(isAdminUser ? '/dashboard' : '/', { replace: true });
          }
        } catch (error) {
          toast.error('Error de autenticación');
          navigate('/', { replace: true });
        }
      } else {
        navigate('/', { replace: true });
      }
    };

    processAuth();
  }, [location, navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F4F0]">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#264639] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-[#57534E] font-medium">Procesando autenticación...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false, requireSuperAdmin = false }) => {
  const { user, loading, isAdmin, isSuperAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F4F0]">
        <div className="w-12 h-12 border-4 border-[#264639] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (requireSuperAdmin && !isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// App Router Component
function AdminRoute({ children }) {
  return <ProtectedRoute requireAdmin>{children}</ProtectedRoute>;
}

function SuperAdminRoute({ children }) {
  return <ProtectedRoute requireSuperAdmin>{children}</ProtectedRoute>;
}

function UserRoute({ children }) {
  return <ProtectedRoute>{children}</ProtectedRoute>;
}

// Smart dashboard redirect based on role
function SmartDashboard() {
  const { isAdmin } = useAuth();
  return isAdmin ? <DashboardPage /> : <GuestDashboardPage />;
}

function LazyFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F4F0]">
      <div className="text-center">
        <div className="w-10 h-10 border-4 border-[#264639] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-sm text-[#78716C]">Cargando...</p>
      </div>
    </div>
  );
}

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function ShortLinkRedirect() {
  const { slug } = useParams();
  useEffect(() => {
    window.location.replace(`${process.env.REACT_APP_BACKEND_URL}/api/r/${encodeURIComponent(slug)}`);
  }, [slug]);
  return <LazyFallback />;
}


function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
    <ScrollToTop />
    <Suspense fallback={<LazyFallback />}>
      <Routes>
        {/* Public - static imports */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/properties" element={<PropertiesPage />} />
        <Route path="/properties/:id" element={<PropertyDetailPage />} />
        <Route path="/map" element={<MapPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/publicaciónes" element={<PostsPage />} />
        <Route path="/publicaciónes/:id" element={<PostDetailPage />} />
        <Route path="/sumar-alojamiento" element={<OwnerPage />} />
        <Route path="/payment-success" element={<PaymentSuccessPage />} />
        <Route path="/pago-bancard" element={<BancardCheckoutPage />} />
        <Route path="/branding-preview" element={<BrandingPreviewPublicPage />} />
        <Route path="/qr" element={<QrLandingPage />} />
        <Route path="/r/:slug" element={<ShortLinkRedirect />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/gestion/*" element={<GestionApp />} />

        {/* Authenticated User - lazy loaded */}
        <Route path="/dashboard" element={<UserRoute><SmartDashboard /></UserRoute>} />
        <Route path="/mi-cuenta" element={<UserRoute><GuestDashboardPage /></UserRoute>} />
        <Route path="/mis-reservas" element={<UserRoute><GuestReservationsPage /></UserRoute>} />
        <Route path="/mis-reservas/:reservationId" element={<UserRoute><ReservationDetailPage /></UserRoute>} />
        <Route path="/proximos-viajes" element={<UserRoute><UpcomingTripsPage /></UserRoute>} />
        <Route path="/chat" element={<UserRoute><ChatPage /></UserRoute>} />
        <Route path="/wishlist" element={<UserRoute><WishlistPage /></UserRoute>} />
        <Route path="/verification" element={<UserRoute><VerificationPage /></UserRoute>} />
        <Route path="/notifications" element={<UserRoute><NotificationsPage /></UserRoute>} />
        <Route path="/checkin" element={<UserRoute><CheckInPage /></UserRoute>} />

        {/* Admin - lazy loaded */}
        <Route path="/reservations" element={<AdminRoute><ReservationsPage /></AdminRoute>} />
        <Route path="/calendar" element={<AdminRoute><CalendarPage /></AdminRoute>} />
        <Route path="/settings" element={<AdminRoute><SettingsPage /></AdminRoute>} />
        <Route path="/reports" element={<AdminRoute><ReportsPage /></AdminRoute>} />
        <Route path="/admin/properties" element={<AdminRoute><AdminPropertiesPage /></AdminRoute>} />
        <Route path="/admin/coupons" element={<AdminRoute><CouponsPage /></AdminRoute>} />
        <Route path="/admin/emails" element={<AdminRoute><EmailsPage /></AdminRoute>} />
        <Route path="/admin/branding" element={<AdminRoute><BrandingPage /></AdminRoute>} />
        <Route path="/admin/events" element={<AdminRoute><EventsPage /></AdminRoute>} />
        <Route path="/admin/posts" element={<AdminRoute><AdminPostsPage /></AdminRoute>} />
        <Route path="/admin/airbnb-import" element={<AdminRoute><AirbnbImportPage /></AdminRoute>} />
        <Route path="/admin/booking-requests" element={<AdminRoute><AdminBookingRequestsPage /></AdminRoute>} />
        <Route path="/admin/instagram" element={<AdminRoute><InstagramPage /></AdminRoute>} />
        <Route path="/admin/qr" element={<AdminRoute><QrStatsPage /></AdminRoute>} />
        <Route path="/admin/seasonal-coupons" element={<AdminRoute><SeasonalCouponsPage /></AdminRoute>} />
        <Route path="/admin/newsletter" element={<AdminRoute><NewsletterPage /></AdminRoute>} />
        <Route path="/admin/payment-data" element={<AdminRoute><PaymentDataPage /></AdminRoute>} />
        <Route path="/admin/files" element={<AdminRoute><FilesPage /></AdminRoute>} />

        {/* Super Admin - lazy loaded */}
        <Route path="/users" element={<SuperAdminRoute><UsersPage /></SuperAdminRoute>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
    </>
  );
}

function ExchangeRateLoader({ children }) {
  const { setExchangeRates } = useI18n();
  useEffect(() => {
    api.get('/exchange-rates').then(res => {
      const rates = {};
      (res.data || []).forEach(r => {
        if (r.currency === 'USD') rates['PYG'] = r.sell_rate;
        else if (r.currency === 'BRL') rates['BRL'] = r.sell_rate;
        else if (r.currency === 'ARS') rates['ARS'] = r.sell_rate;
      });
      if (rates['PYG'] && rates['BRL']) rates['BRL'] = rates['PYG'] / rates['BRL'];
      if (rates['PYG'] && rates['ARS']) rates['ARS'] = rates['PYG'] / rates['ARS'];
      setExchangeRates(rates);
    }).catch(() => {});
  }, [setExchangeRates]);
  return children;
}

function App() {
  return (
    <ErrorBoundary>
      <div className="App">
        <BrowserRouter>
          <ThemeProvider>
            <I18nProvider>
              <ExchangeRateLoader>
                <BrandingProvider>
                  <AuthProvider>
                    <AppRouter />
                    <WhatsAppBubble />
                    <PropertyPopup />
                    <Toaster position="top-right" richColors />
                  </AuthProvider>
                </BrandingProvider>
              </ExchangeRateLoader>
            </I18nProvider>
          </ThemeProvider>
        </BrowserRouter>
      </div>
    </ErrorBoundary>
  );
}

export default App;
export { api, API };
